import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button, ButtonLink, Checkboxes, DateInput, Details, ErrorSummary, Fieldset, Input, InsetText, Radios, RequiredFieldsNote, Select, Textarea, isoToParts, partsToIso, type ErrorSummaryItem } from '../../../components/idsk';
import { RuleSummarizer, isDecimal, isoDate, mergeDefinition, type Constraints, type EvaluationContext, type FieldDef, type FieldType, type FormDefinition, type OptionDef, type RuleName } from '../../../form-engine';
import { canBeRequired, canHaveChart, canHaveValidation, text, type LocalizedText, type TableHeader } from '../../../form-engine/types';
import { isApiError } from '../../../api/errors';
import { RuleEditor, ruleComplete, leafComplete } from './RuleEditor';
import { KEY_PATTERN, addCustomField, addField, fromEditor, locate, referenceCandidates, replaceField, suggestKey, suggestOptionValue, tableLock, toEditor, validateFor, type EditorRule } from './definitionOps';
import { flatten } from '../../../form-engine/merge';

export type BuilderMode = 'template' | 'custom';

/** The heading of the properties pane (the focus goes there when a field is opened). */
export const PROPERTIES_HEADING = 'field-properties-heading';
/** The form of the properties ("Uložiť pole" in the action bar submits it through the form attribute). */
const PROPERTIES_FORM = 'field-properties-form';

const RULES: RuleName[] = ['visibleWhen', 'enabledWhen', 'requiredWhen'];
const WIDTHS = ['full', 'three-quarters', 'two-thirds', 'one-half', 'one-third', 'one-quarter', '30', '20', '10', '5'];
/** a list (select) takes a fraction of the column only */
const FLUID_WIDTHS = WIDTHS.slice(0, 6);

interface OptionDraft {
  id: string;
  value: string;
  sk: string;
  en: string;
  locked: boolean;
  /** when the option is offered (null: always) */
  rule: EditorRule | null | 'unsupported';
}

/** A column or row header of a TABLE; `key` (C1, C2 … / R1, R2 …) names its cells in the stored values. */
interface HeaderDraft {
  id: string;
  key: string;
  sk: string;
  en: string;
  /** published or with stored values: cannot be removed */
  locked: boolean;
}

interface TableDraft {
  cornerSk: string;
  cornerEn: string;
  columns: HeaderDraft[];
  rows: HeaderDraft[];
}

type HeaderKind = 'column' | 'row';
/** columns 1–20, rows 1–50 (form-definition.schema.json) */
const TABLE_MAX: Record<HeaderKind, number> = { column: 20, row: 50 };
const KEY_PREFIX: Record<HeaderKind, string> = { column: 'C', row: 'R' };

/** The next key: C1, C2 … (columns) or R1, R2 … (rows), after the highest one and never one in use. */
function nextHeaderKey(kind: HeaderKind, headers: HeaderDraft[]): string {
  const prefix = KEY_PREFIX[kind];
  const used = new Set(headers.map((h) => h.key));
  let n = Math.max(0, ...headers.map((h) => (h.key.startsWith(prefix) && /^\d+$/.test(h.key.slice(1)) ? Number(h.key.slice(1)) : 0))) + 1;
  while (used.has(`${prefix}${n}`)) n++;
  return `${prefix}${n}`;
}

function newHeader(kind: HeaderKind, headers: HeaderDraft[]): HeaderDraft {
  return { id: `h${++optionCounter}`, key: nextHeaderKey(kind, headers), sk: '', en: '', locked: false };
}

/** The headers of the field; a new table has two columns and two rows, every header still to be named. */
function toTableDraft(field: FieldDef | null, lockedOptions: Set<string>): TableDraft {
  const table = field?.table;
  if (!table) {
    const columns = [newHeader('column', [])];
    columns.push(newHeader('column', columns));
    const rows = [newHeader('row', [])];
    rows.push(newHeader('row', rows));
    return { cornerSk: '', cornerEn: '', columns, rows };
  }
  const headers = (kind: HeaderKind, list: TableHeader[]) =>
    list.map((h) => ({ id: `h${++optionCounter}`, key: h.key, sk: h.label.sk, en: h.label.en ?? '', locked: lockedOptions.has(tableLock(kind, h.key)) }));
  return { cornerSk: table.corner.sk, cornerEn: table.corner.en ?? '', columns: headers('column', table.columns), rows: headers('row', table.rows) };
}

function localized(sk: string, en: string): LocalizedText {
  return en.trim() ? { sk: sk.trim(), en: en.trim() } : { sk: sk.trim() };
}

interface Draft {
  key: string;
  keyTouched: boolean;
  labelSk: string;
  labelEn: string;
  hintSk: string;
  hintEn: string;
  required: boolean;
  /** "Zobraziť v grafe" (D31) */
  chart: boolean;
  width: string;
  constraints: Record<string, string>;
  options: OptionDraft[];
  table: TableDraft;
  placeAfter: string;
  rules: Record<RuleName, EditorRule | null | 'unsupported'>;
  /** the error shown when the validation rule (validWhen) does not hold */
  messageSk: string;
  messageEn: string;
}

let optionCounter = 0;

function toDraft(field: FieldDef | null, lockedOptions: Set<string>, defaultAnchor: string, initialConstraints: Record<string, string> = {}): Draft {
  const c = (field?.constraints ?? initialConstraints) as Record<string, unknown>;
  return {
    key: field?.key ?? '',
    keyTouched: Boolean(field),
    labelSk: field?.label.sk ?? '',
    labelEn: field?.label.en ?? '',
    hintSk: field?.hint?.sk ?? '',
    hintEn: field?.hint?.en ?? '',
    required: Boolean(field?.required),
    chart: Boolean(field?.chart),
    width: field?.width ?? '',
    constraints: Object.fromEntries(Object.entries(c).map(([k, v]) => [k, String(v)])),
    options: (field?.options ?? []).map((o) => ({ id: `o${++optionCounter}`, value: o.value, sk: o.label.sk, en: o.label.en ?? '', locked: lockedOptions.has(o.value), rule: toEditor(o.visibleWhen) })),
    table: toTableDraft(field, lockedOptions),
    placeAfter: field ? (field.placeAfter ?? '') : defaultAnchor,
    rules: { visibleWhen: toEditor(field?.visibleWhen), enabledWhen: toEditor(field?.enabledWhen), requiredWhen: toEditor(field?.requiredWhen), validWhen: toEditor(field?.validWhen) },
    messageSk: field?.validationMessage?.sk ?? '',
    messageEn: field?.validationMessage?.en ?? '',
  };
}

const INT_SETTINGS = ['maxLength', 'rows', 'decimals', 'minSelected', 'maxSelected', 'minRows', 'maxRows'];

export interface FieldEditorProps {
  mode: BuilderMode;
  type: FieldType;
  /** template mode: the draft; custom mode: the pinned template definition */
  definition: FormDefinition;
  customFields: FieldDef[];
  existing: FieldDef | null;
  parentKey: string | null;
  /** keys, options and table headers (`tableLock`) that are published or have data: immutable */
  lockedKeys: Set<string>;
  lockedOptions: Set<string>;
  context: EvaluationContext;
  backTo: string;
  /** id of the element focused after closing the pane (the field's link on the canvas) */
  backFocus?: string;
  /** Persists the new definition (template) or custom fields (custom). */
  onSave: (result: { definition: FormDefinition; customFields: FieldDef[]; field: FieldDef }) => Promise<void>;
  /** Settings of a new field chosen in the palette (e.g. a dropdown = SINGLE_CHOICE with display "select"). */
  initialConstraints?: Record<string, string>;
  /**
   * Place of a new field dropped from the palette: index among the fields of `parentKey` (custom mode without a
   * parent: in the effective top level, i.e. the preselected "Umiestniť za poľom"); null/omitted: at the end.
   */
  position?: number | null;
  /** The field as it would be saved, after every change (the canvas shows it live). */
  onPreview?: (field: FieldDef) => void;
  /** Buttons of an existing field (move, remove), in the action bar next to "Uložiť pole" and "Zrušiť". */
  actions?: ReactNode;
  /** Links of an existing field ("Presunúť do sekcie", …), under the heading. */
  links?: ReactNode;
}

/**
 * The properties pane of the form builder (templates: ADMIN; custom fields: team members): beside the canvas, for the
 * field clicked there or a new control from the palette. Every change is shown on the canvas at once (`onPreview`);
 * "Uložiť pole" saves, "Zrušiť" / "Zavrieť" (`backTo`) leaves the field as it was.
 */
export function FieldEditor({ mode, type, definition, customFields, existing, parentKey, lockedKeys, lockedOptions, context, backTo, backFocus, onSave, initialConstraints, position, onPreview, actions, links }: FieldEditorProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage === 'en' ? 'en' : 'sk';
  const effective = mode === 'template' ? definition : mergeDefinition(definition, customFields);
  const topLevel = effective.fields.filter((f) => f.key !== existing?.key);
  // a new field goes to `position` of its list (clamped), else to the end
  const parent = !existing && parentKey ? (locate(effective.fields, parentKey)?.field ?? null) : null;
  const siblings = parent ? (parent.children ?? []) : topLevel;
  const index = position === null || position === undefined || existing ? null : Math.min(Math.max(position, 0), siblings.length);
  const defaultAnchor = index === null ? (topLevel[topLevel.length - 1]?.key ?? '') : (topLevel[index - 1]?.key ?? '');
  const [draft, setDraft] = useState<Draft>(() => toDraft(existing, lockedOptions, defaultAnchor, initialConstraints));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [summary, setSummary] = useState<ErrorSummaryItem[]>([]);
  const [submitCount, setSubmitCount] = useState(0);
  const [saving, setSaving] = useState(false);
  // a switch always has a state, so "required" does not apply to it
  const requirable = canBeRequired(type);
  // administrators show the numbers of a table, repeating group or section in charts of finished inspections (D31)
  const chartable = mode === 'template' && canHaveChart(type);
  // a custom field inside the team's own section has no anchor ("Za poľom …"): it belongs to the section
  const nestedCustom = mode === 'custom' && (parentKey !== null || Boolean(existing && locate(customFields, existing.key)?.parent));
  const isChoice = type === 'SINGLE_CHOICE' || type === 'MULTI_CHOICE';
  const title = existing ? t('builder.editor.editTitle', { label: text(existing.label, lang) }) : t('builder.editor.newTitle', { type: t(`builder.types.${type}.name`) });

  const takenKeys = useMemo(() => new Set(flatten(effective.fields).map((f) => f.key).filter((k) => k !== existing?.key)), [effective, existing]);
  const set = (changes: Partial<Draft>) => setDraft((d) => ({ ...d, ...changes }));
  const keyFor = (d: Draft) => (d.keyTouched ? d.key : suggestKey(d.labelSk, takenKeys));

  /** The field as it would be stored. */
  const build = (d: Draft): FieldDef => {
    const constraints: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(d.constraints)) {
      if (v === '' || v === undefined) continue;
      constraints[k] = INT_SETTINGS.includes(k) ? Number(v) : v;
    }
    const field: FieldDef = {
      key: keyFor(d),
      type,
      label: d.labelEn.trim() ? { sk: d.labelSk.trim(), en: d.labelEn.trim() } : { sk: d.labelSk.trim() },
      origin: mode === 'template' ? 'TEMPLATE' : 'INSPECTION',
    };
    if (requirable) field.required = d.required;
    if (chartable && d.chart) field.chart = true;
    if (d.hintSk.trim()) field.hint = d.hintEn.trim() ? { sk: d.hintSk.trim(), en: d.hintEn.trim() } : { sk: d.hintSk.trim() };
    if (Object.keys(constraints).length) field.constraints = constraints as Constraints;
    if (isChoice) {
      field.options = d.options.map((o): OptionDef => {
        const option: OptionDef = { value: o.value || suggestOptionValue(o.sk, new Set()), label: o.en.trim() ? { sk: o.sk.trim(), en: o.en.trim() } : { sk: o.sk.trim() } };
        if (o.rule === 'unsupported') {
          const before = existing?.options?.find((x) => x.value === o.value)?.visibleWhen;
          if (before) option.visibleWhen = before;
        } else if (o.rule && ruleComplete(o.rule)) option.visibleWhen = fromEditor(o.rule);
        return option;
      });
    }
    if (type === 'TABLE') {
      const headers = (list: HeaderDraft[]): TableHeader[] => list.map((h) => ({ key: h.key, label: localized(h.sk, h.en) }));
      field.table = { corner: localized(d.table.cornerSk, d.table.cornerEn), columns: headers(d.table.columns), rows: headers(d.table.rows) };
    }
    // a list (select) has a width and – for several choices – a number of lines; radios and checkboxes have neither
    const list = isChoice && constraints.display === 'select';
    if (type === 'MULTI_CHOICE' && !list) delete constraints.rows;
    if (isChoice && Object.keys(constraints).length === 0) delete field.constraints;
    if (d.width && (type === 'TEXT' || type === 'NUMBER' || (list && FLUID_WIDTHS.includes(d.width)))) field.width = d.width;
    // the validation rule (and its message) of a field with a simple value (D26)
    for (const r of canHaveValidation(type) ? [...RULES, 'validWhen' as const] : RULES) {
      const rule = d.rules[r];
      if (rule === 'unsupported') {
        if (existing?.[r]) field[r] = existing[r];
      } else if (rule && ruleComplete(rule)) field[r] = fromEditor(rule);
    }
    if (field.validWhen && d.messageSk.trim()) field.validationMessage = localized(d.messageSk, d.messageEn);
    if (mode === 'custom' && !nestedCustom) field.placeAfter = d.placeAfter || null;
    if (existing?.children) field.children = existing.children;
    if (!existing && (type === 'SECTION' || type === 'REPEATING_GROUP')) field.children = [];
    return field;
  };

  const place = (field: FieldDef): { definition: FormDefinition; customFields: FieldDef[] } => {
    if (mode === 'template') {
      return { definition: existing ? replaceField(definition, existing.key, field) : addField(definition, field, parentKey, index), customFields };
    }
    if (existing) return { definition, customFields: replaceField({ fields: customFields }, existing.key, field).fields };
    if (parentKey) return { definition, customFields: addField({ fields: customFields }, field, parentKey, index).fields };
    // right after the chosen field ("Umiestniť za poľom")
    return { definition, customFields: addCustomField(customFields, field) };
  };

  /** Where a new field goes, in words (template mode and fields of the team's own groups; else the anchor select). */
  const placement = (() => {
    if (existing || (mode === 'custom' && !nestedCustom)) return null;
    const at = index ?? siblings.length;
    const after = at > 0 ? text(siblings[at - 1]!.label, lang) : null;
    if (!parent) return at >= siblings.length ? t('builder.editor.placement.end') : after ? t('builder.editor.placement.after', { after }) : t('builder.editor.placement.first');
    const group = t(parent.type === 'SECTION' ? 'builder.editor.placement.section' : 'builder.editor.placement.group', { label: text(parent.label, lang) });
    if (siblings.length === 0) return t('builder.editor.placement.into', { group });
    if (at >= siblings.length) return t('builder.editor.placement.endOf', { group });
    return after ? t('builder.editor.placement.afterIn', { group, after }) : t('builder.editor.placement.firstOf', { group });
  })();

  // rule candidates: earlier fields relative to the field's (future) position
  const preview = useMemo(() => {
    const field = build(draft);
    const placed = place(field);
    const eff = mode === 'template' ? placed.definition : mergeDefinition(placed.definition, placed.customFields);
    const candidates = referenceCandidates(eff, field.key, existing ? null : parentKey);
    // a validation rule may also refer to the field itself (in a repeating group: to its value in the row)
    const self = { ref: locate(eff.fields, field.key)?.parent?.type === 'REPEATING_GROUP' ? `row.${field.key}` : field.key, field, sameRow: false, self: true };
    return { field, eff, candidates, validationCandidates: [self, ...candidates] };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- derived from the draft
  }, [draft]);
  useEffect(() => {
    onPreview?.(preview.field);
  }, [preview.field, onPreview]);
  const summarizer = new RuleSummarizer(effective, context);

  const validate = (): { items: ErrorSummaryItem[]; byId: Record<string, string> } => {
    const items: ErrorSummaryItem[] = [];
    const byId: Record<string, string> = {};
    const add = (id: string, message: string) => {
      byId[id] = message;
      items.push({ targetId: id, text: message });
    };
    if (!draft.labelSk.trim()) add('field-labelSk', t(type === 'INFO' ? 'builder.editor.errors.textRequired' : 'builder.editor.errors.labelRequired'));
    const max = type === 'INFO' ? 500 : 200;
    if (draft.labelSk.length > max) add('field-labelSk', t('builder.editor.errors.tooLong', { max }));
    if (draft.labelEn.length > max) add('field-labelEn', t('builder.editor.errors.tooLong', { max }));
    if (draft.hintSk.length > 500) add('field-hintSk', t('builder.editor.errors.tooLong', { max: 500 }));
    if (draft.hintEn.length > 500) add('field-hintEn', t('builder.editor.errors.tooLong', { max: 500 }));
    const key = keyFor(draft);
    if (!KEY_PATTERN.test(key)) add('field-key', t('builder.editor.errors.keyPattern'));
    else if (takenKeys.has(key)) add('field-key', t('builder.editor.errors.keyTaken'));
    // the lines of a list do not apply to checkboxes (left out when saving)
    const listRows = type !== 'MULTI_CHOICE' || draft.constraints.display === 'select';
    for (const k of INT_SETTINGS) {
      if (k === 'rows' && !listRows) continue;
      const v = draft.constraints[k];
      if (v && !/^\d+$/.test(v)) add(`field-${k}`, t('builder.editor.errors.integer'));
      else if (k === 'rows' && v && (Number(v) < 2 || Number(v) > 20)) add('field-rows', t('builder.editor.errors.rowsRange'));
    }
    for (const k of ['min', 'max']) {
      const v = draft.constraints[k];
      if (!v) continue;
      if (type === 'NUMBER' && !isDecimal(v)) add(`field-${k}`, t('builder.editor.errors.decimal'));
      if (type === 'DATE' && !isoDate(v)) add(`field-${k}-day`, t('builder.editor.errors.date'));
    }
    if (isChoice) {
      const minOptions = type === 'SINGLE_CHOICE' ? 2 : 1;
      if (draft.options.length < minOptions) add('field-options-add', t('builder.editor.errors.optionsMin', { count: minOptions }));
      const values = new Set<string>();
      draft.options.forEach((o, i) => {
        if (!o.sk.trim()) add(`option-${i + 1}-sk`, t('builder.editor.errors.optionLabel', { n: i + 1 }));
        const v = o.value || suggestOptionValue(o.sk, new Set());
        if (!/^[A-Za-z0-9_]{1,40}$/.test(v)) add(`option-${i + 1}-value`, t('builder.editor.errors.optionValue', { n: i + 1 }));
        else if (values.has(v)) add(`option-${i + 1}-value`, t('builder.editor.errors.optionDuplicate', { n: i + 1 }));
        values.add(v);
      });
    }
    if (type === 'TABLE') {
      // every header is required: the corner, the first row (columns) and the first column (rows)
      const header = (id: string, sk: string, en: string, missing: string) => {
        if (!sk.trim()) add(`${id}-sk`, missing);
        else if (sk.length > 200) add(`${id}-sk`, t('builder.editor.errors.tooLong', { max: 200 }));
        if (en.length > 200) add(`${id}-en`, t('builder.editor.errors.tooLong', { max: 200 }));
      };
      header('table-corner', draft.table.cornerSk, draft.table.cornerEn, t('builder.table.cornerError'));
      draft.table.columns.forEach((h, i) => header(`table-column-${i + 1}`, h.sk, h.en, t('builder.table.column.error', { n: i + 1 })));
      draft.table.rows.forEach((h, i) => header(`table-row-${i + 1}`, h.sk, h.en, t('builder.table.row.error', { n: i + 1 })));
    }
    const ruleRows = (rule: EditorRule | null | 'unsupported', base: string) => {
      if (!rule || rule === 'unsupported') return;
      rule.items.forEach((item, i) => {
        const leaves = item.kind === 'leaf' ? [{ leaf: item, id: `${base}-${i + 1}` }] : item.items.map((l, j) => ({ leaf: l, id: `${base}-${i + 1}-${j + 1}` }));
        for (const { leaf, id } of leaves) {
          if (!leaf.field) add(`${id}-field`, t('builder.rules.errors.field'));
          else if (!leaf.op) add(`${id}-op`, t('builder.rules.errors.op'));
          else if (!leafComplete(leaf)) {
            if (leaf.valueField !== undefined) add(`${id}-valueField`, t('builder.rules.errors.valueField'));
            else add(`${id}-value`, t('builder.rules.errors.value'));
          }
        }
      });
    };
    for (const r of RULES) ruleRows(draft.rules[r], `rule-${r}`);
    if (canHaveValidation(type) && draft.rules.validWhen !== null) {
      ruleRows(draft.rules.validWhen, 'rule-validWhen');
      if (draft.messageSk.length > 300) add('field-messageSk', t('builder.editor.errors.tooLong', { max: 300 }));
      else if (!draft.messageSk.trim() && draft.messageEn.trim()) add('field-messageSk', t('builder.validation.errors.messageSk'));
      if (draft.messageEn.length > 300) add('field-messageEn', t('builder.editor.errors.tooLong', { max: 300 }));
    }
    if (isChoice) draft.options.forEach((o, i) => ruleRows(o.rule, `option-${i + 1}-rule`));
    if (items.length === 0) {
      // the engine's semantic checks over the whole definition
      const field = build(draft);
      const placed = place(field);
      const eff = mode === 'template' ? placed.definition : mergeDefinition(placed.definition, placed.customFields);
      const defErrors = validateFor(mode, eff, definition, placed.customFields).filter((e) => e.field === field.key || e.field === '');
      for (const e of defErrors) {
        const optionIndex = e.option !== undefined ? draft.options.findIndex((o) => (o.value || suggestOptionValue(o.sk, new Set())) === e.option) : -1;
        const target =
          optionIndex >= 0
            ? `option-${optionIndex + 1}-rule-mode`
            : e.rule
              ? `rule-${e.rule}-mode`
              : e.code.startsWith('option')
                ? 'field-options-add'
                : e.code.startsWith('table')
                  ? 'table-corner-sk'
                  : e.code.startsWith('chart')
                    ? 'field-chart'
                    : e.code.startsWith('key')
                    ? 'field-key'
                    : 'field-labelSk';
        add(target, t(`builder.defErrors.${e.code.replace('.', '_')}`, { defaultValue: t('builder.defErrors.generic', { code: e.code }) }));
      }
    }
    return { items, byId };
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const { items, byId } = validate();
    setErrors(byId);
    setSummary(items);
    setSubmitCount((c) => c + 1);
    if (items.length) return;
    const field = build(draft);
    setSaving(true);
    try {
      await onSave({ ...place(field), field });
    } catch (e) {
      setSubmitCount((c) => c + 1);
      setSummary(isApiError(e) && e.fieldErrors.length ? e.fieldErrors.map((fe) => ({ text: fe.message })) : [{ text: isApiError(e) ? e.message : t('errors.unexpected') }]);
    } finally {
      setSaving(false);
    }
  };

  const constraint = (k: string) => draft.constraints[k] ?? '';
  const setConstraint = (k: string, v: string) => set({ constraints: { ...draft.constraints, [k]: v } });
  const keyLocked = Boolean(existing) && lockedKeys.has(existing!.key);

  return (
    <div className="app-properties-panel">
      <section className="app-properties" aria-labelledby={PROPERTIES_HEADING}>
        {summary.length > 0 && <ErrorSummary errors={summary} focusKey={submitCount} />}
        <div className="app-properties__header">
          <span className="govuk-caption-m">{t(`builder.types.${type}.name`)}</span>
          <h2 className="govuk-heading-m" id={PROPERTIES_HEADING} tabIndex={-1}>
            {title}
          </h2>
          <Link className="govuk-link app-properties__close" to={backTo} state={backFocus ? { focus: backFocus } : undefined}>
            {t('builder.properties.close')}
          </Link>
        </div>
        {links}
        <p className="govuk-body">{t(`builder.types.${type}.description`)}</p>
        {placement && <InsetText id="field-placement">{placement}</InsetText>}
        <form id={PROPERTIES_FORM} onSubmit={(e) => void submit(e)} noValidate>
          <RequiredFieldsNote />
          <Fieldset legend={<h3 className="govuk-fieldset__heading">{t('builder.editor.texts')}</h3>} legendClassName="govuk-fieldset__legend--m">
            {type === 'INFO' ? (
              <>
                <Textarea id="field-labelSk" label={t('builder.editor.infoSk')} required error={errors['field-labelSk']} value={draft.labelSk} onChange={(e) => set({ labelSk: e.target.value })} rows={4} />
                <Textarea id="field-labelEn" label={t('builder.editor.infoEn')} hint={t('builder.editor.enHint')} error={errors['field-labelEn']} value={draft.labelEn} onChange={(e) => set({ labelEn: e.target.value })} rows={4} />
              </>
            ) : (
              <>
                <Input id="field-labelSk" label={t('builder.editor.labelSk')} required error={errors['field-labelSk']} value={draft.labelSk} onChange={(e) => set({ labelSk: e.target.value })} />
                <Input id="field-labelEn" label={t('builder.editor.labelEn')} hint={t('builder.editor.enHint')} error={errors['field-labelEn']} value={draft.labelEn} onChange={(e) => set({ labelEn: e.target.value })} />
                <Textarea id="field-hintSk" label={t('builder.editor.hintSk')} rows={2} error={errors['field-hintSk']} value={draft.hintSk} onChange={(e) => set({ hintSk: e.target.value })} />
                <Textarea id="field-hintEn" label={t('builder.editor.hintEn')} rows={2} error={errors['field-hintEn']} value={draft.hintEn} onChange={(e) => set({ hintEn: e.target.value })} />
              </>
            )}
          </Fieldset>

          <Input
            id="field-key"
            label={t('builder.editor.key')}
            hint={keyLocked ? t('builder.editor.keyLocked') : t('builder.editor.keyHint')}
            error={errors['field-key']}
            value={keyFor(draft)}
            readOnly={keyLocked || Boolean(existing)}
            aria-disabled={keyLocked || Boolean(existing) ? true : undefined}
            spellCheck={false}
            width="20"
            onChange={(e) => set({ key: e.target.value, keyTouched: true })}
          />

          {requirable && (
            <Checkboxes
              name="field-required"
              idPrefix="field-required"
              legend={t('builder.editor.requiredLegend')}
              legendClassName="govuk-fieldset__legend--s"
              items={[{ value: 'yes', label: t('builder.editor.required'), hint: t(type === 'TABLE' ? 'builder.editor.requiredTableHint' : 'builder.editor.requiredHint') }]}
              value={draft.required ? ['yes'] : []}
              onChange={(v) => set({ required: v.includes('yes') })}
            />
          )}

          {chartable && (
            <Checkboxes
              name="field-chart"
              idPrefix="field-chart"
              legend={t('builder.editor.chartLegend')}
              legendClassName="govuk-fieldset__legend--s"
              items={[{ value: 'yes', label: t('builder.editor.chart'), hint: t(`builder.editor.chartHint.${type}`) }]}
              value={draft.chart ? ['yes'] : []}
              error={errors['field-chart']}
              onChange={(v) => set({ chart: v.includes('yes') })}
            />
          )}

          {type === 'TABLE' && <TableEditor table={draft.table} errors={errors} onChange={(table) => set({ table })} />}

          <TypeSettings type={type} constraint={constraint} setConstraint={setConstraint} width={draft.width} setWidth={(width) => set({ width })} errors={errors} />

          {isChoice && (
            <OptionsEditor
              options={draft.options}
              errors={errors}
              onChange={(options) => set({ options })}
              rule={(o, i) => (
                <RuleEditor
                  id={`option-${i + 1}-rule`}
                  rule="visibleWhen"
                  legend={t('builder.rules.option.legend', { n: i + 1 })}
                  optionLabel={{ sk: o.sk || t('builder.options.option', { n: i + 1 }), en: o.en || undefined }}
                  value={o.rule}
                  unsupportedSummary={(() => {
                    const before = existing?.options?.find((x) => x.value === o.value);
                    return before ? summarizer.optionSummary(before, lang) : null;
                  })()}
                  onChange={(rule) => set({ options: draft.options.map((x) => (x.id === o.id ? { ...x, rule } : x)) })}
                  candidates={preview.candidates}
                  definition={preview.eff}
                  context={context}
                  teamKnown={mode === 'custom'}
                  errors={errors}
                />
              )}
            />
          )}

          {mode === 'custom' && !nestedCustom && (
            <Select
              id="field-placeAfter"
              label={t('builder.editor.placeAfter')}
              hint={t('builder.editor.placeAfterHint')}
              options={[{ value: '', label: t('builder.editor.placeFirst') }, ...topLevel.map((f) => ({ value: f.key, label: text(f.label, lang) }))]}
              value={draft.placeAfter}
              onChange={(e) => set({ placeAfter: e.target.value })}
            />
          )}

          {RULES.filter((r) => r !== 'requiredWhen' || requirable).map((r) => (
            <RuleEditor
              key={r}
              id={`rule-${r}`}
              rule={r}
              value={draft.rules[r]}
              unsupportedSummary={existing?.[r] ? summarizer.summaryOf(existing, r, lang) : null}
              onChange={(value) => set({ rules: { ...draft.rules, [r]: value } })}
              candidates={preview.candidates}
              definition={preview.eff}
              context={context}
              teamKnown={mode === 'custom'}
              errors={errors}
            />
          ))}

          {canHaveValidation(type) && (
            <RuleEditor
              id="rule-validWhen"
              rule="validWhen"
              hint={t('builder.rules.validWhen.hint')}
              value={draft.rules.validWhen}
              unsupportedSummary={existing?.validWhen ? summarizer.summaryOf(existing, 'validWhen', lang) : null}
              onChange={(value) => set({ rules: { ...draft.rules, validWhen: value } })}
              candidates={preview.validationCandidates}
              definition={preview.eff}
              context={context}
              teamKnown={mode === 'custom'}
              errors={errors}
            >
              <Textarea
                id="field-messageSk"
                label={t('builder.validation.messageSk')}
                hint={t('builder.validation.messageHint')}
                rows={2}
                error={errors['field-messageSk']}
                value={draft.messageSk}
                onChange={(e) => set({ messageSk: e.target.value })}
              />
              <Textarea id="field-messageEn" label={t('builder.validation.messageEn')} rows={2} error={errors['field-messageEn']} value={draft.messageEn} onChange={(e) => set({ messageEn: e.target.value })} />
            </RuleEditor>
          )}

          {existing && locate(effective.fields, existing.key)?.field.children?.length ? (
            <InsetText>{t('builder.editor.childrenNote', { count: locate(effective.fields, existing.key)?.field.children?.length ?? 0 })}</InsetText>
          ) : null}

        </form>
      </section>
      {/* always in view below the properties (the properties scroll above it) */}
      <div className="app-properties__actions" role="group" aria-label={t('builder.properties.actions')}>
        <div className="app-properties__buttons">
          <Button type="submit" form={PROPERTIES_FORM} preventDoubleClick aria-disabled={saving ? true : undefined}>
            {t('builder.editor.save')}
          </Button>
          <ButtonLink to={backTo} state={backFocus ? { focus: backFocus } : undefined} variant="secondary">
            {t('common.cancel')}
          </ButtonLink>
        </div>
        {actions}
      </div>
    </div>
  );
}

function TypeSettings({
  type,
  constraint,
  setConstraint,
  width,
  setWidth,
  errors,
}: {
  type: FieldType;
  constraint: (k: string) => string;
  setConstraint: (k: string, v: string) => void;
  width: string;
  setWidth: (w: string) => void;
  errors: Record<string, string>;
}) {
  const { t } = useTranslation();
  const num = (k: string, hint?: string) => (
    <Input key={k} id={`field-${k}`} label={t(`builder.settings.${k}`)} hint={hint} inputMode="numeric" width="5" error={errors[`field-${k}`]} value={constraint(k)} onChange={(e) => setConstraint(k, e.target.value.trim())} />
  );
  const content = (() => {
    switch (type) {
      case 'TEXT':
        return (
          <>
            {num('maxLength', t('builder.settings.maxLengthHint', { max: 255 }))}
            <Radios
              name="field-format"
              idPrefix="field-format"
              legend={t('builder.settings.format')}
              legendClassName="govuk-fieldset__legend--s"
              items={['none', 'email', 'phone', 'ico', 'psc'].map((v) => ({ value: v, label: t(`builder.settings.formats.${v}`) }))}
              value={constraint('format') || 'none'}
              onChange={(v) => setConstraint('format', v === 'none' ? '' : v)}
            />
            <WidthSelect width={width} setWidth={setWidth} />
          </>
        );
      case 'TEXTAREA':
        return (
          <>
            {num('maxLength', t('builder.settings.maxLengthHint', { max: 8000 }))}
            {num('rows')}
          </>
        );
      case 'RICH_TEXT':
        return num('maxLength', t('builder.settings.maxLengthRichHint', { max: 8000 }));
      case 'TABLE':
        return num('maxLength', t('builder.settings.maxLengthCellHint', { max: 255 }));
      case 'NUMBER':
        return (
          <>
            <Input id="field-min" label={t('builder.settings.min')} inputMode="decimal" width="10" error={errors['field-min']} value={constraint('min')} onChange={(e) => setConstraint('min', e.target.value.trim())} />
            <Input id="field-max" label={t('builder.settings.max')} inputMode="decimal" width="10" error={errors['field-max']} value={constraint('max')} onChange={(e) => setConstraint('max', e.target.value.trim())} />
            {num('decimals')}
            <WidthSelect width={width} setWidth={setWidth} />
          </>
        );
      case 'DATE':
        return (
          <>
            <Radios
              name="field-relative"
              idPrefix="field-relative"
              legend={t('builder.settings.relative')}
              legendClassName="govuk-fieldset__legend--s"
              items={['', 'WITHIN_TIMEFRAME', 'ON_OR_BEFORE_START', 'ON_OR_AFTER_TODAY'].map((v) => ({ value: v || 'NONE', label: t(`builder.settings.relatives.${v || 'NONE'}`) }))}
              value={constraint('relative') || 'NONE'}
              onChange={(v) => setConstraint('relative', v === 'NONE' ? '' : v)}
            />
            <DateSetting id="field-min" legend={t('builder.settings.minDate')} error={errors['field-min-day']} value={constraint('min')} onChange={(iso) => setConstraint('min', iso)} />
            <DateSetting id="field-max" legend={t('builder.settings.maxDate')} error={errors['field-max-day']} value={constraint('max')} onChange={(iso) => setConstraint('max', iso)} />
          </>
        );
      case 'SINGLE_CHOICE':
        return (
          <Radios
            name="field-display"
            idPrefix="field-display"
            legend={t('builder.settings.display')}
            legendClassName="govuk-fieldset__legend--s"
            hint={t('builder.settings.displayHint')}
            items={[
              { value: 'radios', label: t('builder.settings.displays.radios') },
              { value: 'select', label: t('builder.settings.displays.select'), conditional: <WidthSelect width={width} setWidth={setWidth} fluid /> },
            ]}
            value={constraint('display') || 'radios'}
            onChange={(v) => setConstraint('display', v === 'radios' ? '' : v)}
          />
        );
      case 'MULTI_CHOICE':
        return (
          <>
            <Radios
              name="field-display"
              idPrefix="field-display"
              legend={t('builder.settings.display')}
              legendClassName="govuk-fieldset__legend--s"
              hint={t('builder.settings.displayMultiHint')}
              items={[
                { value: 'checkboxes', label: t('builder.settings.displays.checkboxes') },
                {
                  value: 'select',
                  label: t('builder.settings.displays.selectMultiple'),
                  // the size of the list: its width and how many lines it shows at once
                  conditional: (
                    <>
                      <WidthSelect width={width} setWidth={setWidth} fluid />
                      <Input
                        id="field-rows"
                        label={t('builder.settings.listRows')}
                        hint={t('builder.settings.listRowsHint')}
                        inputMode="numeric"
                        width="5"
                        error={errors['field-rows']}
                        value={constraint('rows')}
                        onChange={(e) => setConstraint('rows', e.target.value.trim())}
                      />
                    </>
                  ),
                },
              ]}
              value={constraint('display') || 'checkboxes'}
              onChange={(v) => setConstraint('display', v === 'checkboxes' ? '' : v)}
            />
            {num('minSelected')}
            {num('maxSelected')}
          </>
        );
      case 'AUTOCOMPLETE':
        return (
          <Radios
            name="field-source"
            idPrefix="field-source"
            legend={t('builder.settings.source')}
            legendClassName="govuk-fieldset__legend--s"
            hint={t('builder.settings.sourceHint')}
            items={(['SUBJECT', 'INSPECTOR'] as const).map((v) => ({ value: v, label: t(`builder.settings.sources.${v}`) }))}
            value={constraint('source') || 'SUBJECT'}
            onChange={(v) => setConstraint('source', v)}
          />
        );
      case 'REPEATING_GROUP':
        return (
          <>
            {num('minRows')}
            {num('maxRows')}
          </>
        );
      case 'INFO':
        return (
          <Radios
            name="field-variant"
            idPrefix="field-variant"
            legend={t('builder.settings.variant')}
            legendClassName="govuk-fieldset__legend--s"
            items={[
              { value: 'inset', label: t('builder.settings.variants.inset') },
              { value: 'warning', label: t('builder.settings.variants.warning') },
            ]}
            value={constraint('variant') || 'inset'}
            onChange={(v) => setConstraint('variant', v)}
          />
        );
      default:
        return null;
    }
  })();
  if (!content) return null;
  return (
    <Fieldset legend={<h3 className="govuk-fieldset__heading">{t('builder.editor.settings')}</h3>} legendClassName="govuk-fieldset__legend--m">
      {content}
    </Fieldset>
  );
}

function DateSetting({ id, legend, error, value, onChange }: { id: string; legend: string; error?: string; value: string; onChange: (iso: string) => void }) {
  const [parts, setParts] = useState(() => isoToParts(value));
  return (
    <DateInput
      id={id}
      legend={legend}
      legendClassName="govuk-fieldset__legend--s"
      error={error}
      value={parts}
      onChange={(p) => {
        setParts(p);
        const r = partsToIso(p);
        onChange(r.kind === 'valid' ? r.iso : r.kind === 'empty' ? '' : `${p.year}-${p.month}-${p.day}`);
      }}
    />
  );
}

/** `fluid`: fractions of the column only (a list); else also the character widths of an input. */
function WidthSelect({ width, setWidth, fluid = false }: { width: string; setWidth: (w: string) => void; fluid?: boolean }) {
  const { t } = useTranslation();
  return (
    <Select
      id="field-width"
      label={t('builder.settings.width')}
      placeholder={t('builder.settings.widthDefault')}
      options={(fluid ? FLUID_WIDTHS : WIDTHS).map((w) => ({ value: w, label: t(`builder.settings.widths.${w}`) }))}
      value={width}
      onChange={(e) => setWidth(e.target.value)}
    />
  );
}

function OptionsEditor({ options, errors, onChange, rule }: { options: OptionDraft[]; errors: Record<string, string>; onChange: (o: OptionDraft[]) => void; rule: (o: OptionDraft, index: number) => ReactNode }) {
  const { t } = useTranslation();
  const update = (i: number, changes: Partial<OptionDraft>) => onChange(options.map((o, j) => (j === i ? { ...o, ...changes } : o)));
  return (
    <Fieldset legend={<h3 className="govuk-fieldset__heading">{t('builder.options.legend')}</h3>} legendClassName="govuk-fieldset__legend--m">
      <span className="govuk-hint">{t('builder.options.hint')}</span>
      {options.map((o, i) => (
        <Fieldset key={o.id} legend={t('builder.options.option', { n: i + 1 })} legendClassName="govuk-fieldset__legend--s" className="app-repeating-row" id={`option-${i + 1}`}>
          <Input id={`option-${i + 1}-sk`} label={t('builder.options.labelSk')} required error={errors[`option-${i + 1}-sk`]} value={o.sk} onChange={(e) => update(i, { sk: e.target.value })} />
          <Input id={`option-${i + 1}-en`} label={t('builder.options.labelEn')} value={o.en} onChange={(e) => update(i, { en: e.target.value })} />
          <Input
            id={`option-${i + 1}-value`}
            label={t('builder.options.value')}
            hint={o.locked ? t('builder.options.valueLocked') : t('builder.options.valueHint')}
            readOnly={o.locked}
            aria-disabled={o.locked ? true : undefined}
            width="20"
            spellCheck={false}
            error={errors[`option-${i + 1}-value`]}
            value={o.value}
            placeholder={o.value ? undefined : suggestOptionValue(o.sk, new Set())}
            onChange={(e) => update(i, { value: e.target.value.toUpperCase() })}
          />
          {/* collapsed unless the option has a rule (an error in it then shows) */}
          <Details summary={t('builder.options.ruleSummary', { n: i + 1 })} open={o.rule !== null}>
            {rule(o, i)}
          </Details>
          {!o.locked && (
            <Button
              variant="secondary"
              onClick={() => {
                const next = options.filter((_, j) => j !== i);
                onChange(next);
                requestAnimationFrame(() => document.getElementById(next[i] ? `option-${i + 1}-sk` : 'field-options-add')?.focus());
              }}
            >
              {t('builder.options.remove', { n: i + 1 })}
            </Button>
          )}
        </Fieldset>
      ))}
      <Button
        id="field-options-add"
        variant="secondary"
        onClick={() => {
          onChange([...options, { id: `o${++optionCounter}`, value: '', sk: '', en: '', locked: false, rule: null }]);
          requestAnimationFrame(() => document.getElementById(`option-${options.length + 1}-sk`)?.focus());
        }}
      >
        {t('builder.options.add')}
      </Button>
    </Fieldset>
  );
}


/**
 * The headers of a TABLE (D24): the corner, the columns (first row) and the rows (first column), each with a Slovak
 * (required) and an English text. Columns and rows can be added and removed – at least one of each stays, and so do
 * published ones and ones with stored values. The canvas shows the table as it will look.
 */
function TableEditor({ table, errors, onChange }: { table: TableDraft; errors: Record<string, string>; onChange: (table: TableDraft) => void }) {
  const { t } = useTranslation();
  return (
    <Fieldset legend={<h3 className="govuk-fieldset__heading">{t('builder.table.legend')}</h3>} legendClassName="govuk-fieldset__legend--m" id="table-headers">
      <span className="govuk-hint">{t('builder.table.hint')}</span>
      <Input id="table-corner-sk" label={t('builder.table.cornerSk')} required hint={t('builder.table.cornerHint')} error={errors['table-corner-sk']} value={table.cornerSk} onChange={(e) => onChange({ ...table, cornerSk: e.target.value })} />
      <Input id="table-corner-en" label={t('builder.table.cornerEn')} error={errors['table-corner-en']} value={table.cornerEn} onChange={(e) => onChange({ ...table, cornerEn: e.target.value })} />
      <HeaderList kind="column" headers={table.columns} errors={errors} onChange={(columns) => onChange({ ...table, columns })} />
      <HeaderList kind="row" headers={table.rows} errors={errors} onChange={(rows) => onChange({ ...table, rows })} />
    </Fieldset>
  );
}

function HeaderList({ kind, headers, errors, onChange }: { kind: HeaderKind; headers: HeaderDraft[]; errors: Record<string, string>; onChange: (headers: HeaderDraft[]) => void }) {
  const { t } = useTranslation();
  const max = TABLE_MAX[kind];
  const addId = `table-${kind}s-add`;
  const update = (i: number, changes: Partial<HeaderDraft>) => onChange(headers.map((h, j) => (j === i ? { ...h, ...changes } : h)));
  return (
    <div className="app-table-headers" id={`table-${kind}s`}>
      <h4 className="govuk-heading-s">{t(`builder.table.${kind}.heading`)}</h4>
      {headers.map((h, i) => (
        <Fieldset key={h.id} legend={t(`builder.table.${kind}.item`, { n: i + 1 })} legendClassName="govuk-fieldset__legend--s" className="app-repeating-row" id={`table-${kind}-${i + 1}`}>
          <Input id={`table-${kind}-${i + 1}-sk`} label={t('builder.table.labelSk')} required error={errors[`table-${kind}-${i + 1}-sk`]} value={h.sk} onChange={(e) => update(i, { sk: e.target.value })} />
          <Input id={`table-${kind}-${i + 1}-en`} label={t('builder.table.labelEn')} error={errors[`table-${kind}-${i + 1}-en`]} value={h.en} onChange={(e) => update(i, { en: e.target.value })} />
          {h.locked ? (
            <p className="govuk-hint">{t(`builder.table.${kind}.locked`)}</p>
          ) : (
            headers.length > 1 && (
              <Button
                variant="secondary"
                onClick={() => {
                  const next = headers.filter((_, j) => j !== i);
                  onChange(next);
                  requestAnimationFrame(() => document.getElementById(next[i] ? `table-${kind}-${i + 1}-sk` : addId)?.focus());
                }}
              >
                {t(`builder.table.${kind}.remove`, { n: i + 1 })}
              </Button>
            )
          )}
        </Fieldset>
      ))}
      {headers.length < max ? (
        <Button
          id={addId}
          variant="secondary"
          onClick={() => {
            onChange([...headers, newHeader(kind, headers)]);
            requestAnimationFrame(() => document.getElementById(`table-${kind}-${headers.length + 1}-sk`)?.focus());
          }}
        >
          {t(`builder.table.${kind}.add`)}
        </Button>
      ) : (
        <p className="govuk-body">{t(`builder.table.${kind}.max`, { count: max })}</p>
      )}
    </div>
  );
}
